import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import {
  Car,
  MapPin,
  Calendar,
  Clock,
  Users,
  Package,
  Star,
  Phone,
  Mail,
  MessageSquare,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Shield
} from 'lucide-react';

export default function CarpoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  
  // Booking form state
  const [bookingType, setBookingType] = useState('passenger');
  const [seatsToBook, setSeatsToBook] = useState(1);
  const [cargoWeight, setCargoWeight] = useState(0);
  const [pickupPoint, setPickupPoint] = useState('');
  const [dropPoint, setDropPoint] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await api.get(`/carpool/listings/${id}`);
        setListing(response.data);
        setPickupPoint(response.data.origin);
        setDropPoint(response.data.destination);
      } catch (error) {
        toast.error('Failed to load ride details');
        navigate('/carpool');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id, navigate]);

  const calculatePrice = () => {
    if (!listing) return { total: 0 };
    const seatPrice = bookingType === 'passenger' || seatsToBook > 0
      ? listing.price_per_seat * seatsToBook
      : 0;
    const cargoPrice = bookingType === 'cargo' || cargoWeight > 0
      ? listing.price_per_kg * cargoWeight
      : 0;
    const subtotal = seatPrice + cargoPrice;
    const platformFee = Math.round(subtotal * 0.02 * 100) / 100;
    return {
      seatPrice,
      cargoPrice,
      subtotal,
      platformFee,
      total: subtotal + platformFee
    };
  };

  const handleBook = async () => {
    if (!user) {
      toast.error('Please login to book');
      navigate('/auth?tab=login');
      return;
    }

    if (bookingType === 'passenger' && seatsToBook < 1) {
      toast.error('Select at least 1 seat');
      return;
    }

    if (bookingType === 'cargo' && cargoWeight < 1) {
      toast.error('Enter cargo weight');
      return;
    }

    setBooking(true);
    try {
      const response = await api.post('/carpool/book', {
        listing_id: id,
        booking_type: bookingType,
        seats_booked: bookingType === 'passenger' ? seatsToBook : 0,
        cargo_weight_kg: bookingType === 'cargo' ? cargoWeight : 0,
        pickup_point: pickupPoint,
        drop_point: dropPoint,
        notes
      });
      toast.success('Booking request sent!');
      navigate('/dashboard?tab=carpool');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const vehicleIcons = {
    car: '🚗',
    suv: '🚙',
    van: '🚐',
    bike: '🏍️'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!listing) return null;

  const pricing = calculatePrice();
  const isOwnListing = user?.user_id === listing.driver_id;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-accent text-white py-6">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/carpool" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to listings
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-4xl">{vehicleIcons[listing.vehicle_type] || '🚗'}</div>
            <div>
              <h1 className="text-2xl font-black">
                {listing.origin} → {listing.destination}
              </h1>
              <p className="opacity-90">
                {formatDate(listing.departure_date)} at {listing.departure_time}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Driver Info */}
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Driver</h2>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <div>
                  <p className="font-bold text-lg">{listing.driver_name}</p>
                  {listing.driver_type === 'partner' && (
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <Shield className="w-4 h-4" />
                      Verified Partner
                    </span>
                  )}
                  {listing.rating > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{listing.rating.toFixed(1)}</span>
                      <span className="text-sm text-muted-foreground">
                        ({listing.total_reviews} reviews)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Vehicle</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{listing.vehicle_type}</p>
                </div>
                {listing.vehicle_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Model</p>
                    <p className="font-medium">{listing.vehicle_name}</p>
                  </div>
                )}
                {listing.vehicle_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Number</p>
                    <p className="font-medium">{listing.vehicle_number}</p>
                  </div>
                )}
              </div>

              {listing.amenities?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.amenities.map((amenity) => (
                      <span
                        key={amenity}
                        className="px-3 py-1 bg-accent/10 text-accent text-sm capitalize"
                      >
                        {amenity.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Route Details */}
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Route</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{listing.origin}</p>
                    <p className="text-sm text-muted-foreground">Pickup point</p>
                  </div>
                </div>

                {listing.stops?.length > 0 && (
                  <div className="pl-4 border-l-2 border-dashed border-border ml-4">
                    {listing.stops.map((stop, index) => (
                      <div key={index} className="flex items-center gap-3 py-2">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full" />
                        <p className="text-sm">{stop}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium">{listing.destination}</p>
                    <p className="text-sm text-muted-foreground">Drop point</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            {listing.reviews?.length > 0 && (
              <div className="border border-border p-6">
                <h2 className="text-lg font-bold mb-4">Reviews</h2>
                <div className="space-y-4">
                  {listing.reviews.map((review) => (
                    <div key={review.id} className="border-b border-border pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">{review.reviewer_name}</span>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.review && (
                        <p className="text-sm text-muted-foreground">{review.review}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {listing.notes && (
              <div className="border border-border p-6">
                <h2 className="text-lg font-bold mb-4">Additional Notes</h2>
                <p className="text-muted-foreground">{listing.notes}</p>
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="border border-border p-6 sticky top-24">
              <h2 className="text-lg font-bold mb-4">Book this ride</h2>

              {isOwnListing ? (
                <div className="text-center py-4 bg-muted/30">
                  <p className="text-muted-foreground">This is your ride listing</p>
                  <Link to="/carpool/my-rides">
                    <Button variant="outline" className="mt-2 rounded-none">
                      Manage Your Rides
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {/* Booking Type */}
                  <div className="mb-4">
                    <Label>What do you need?</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Button
                        type="button"
                        variant={bookingType === 'passenger' ? 'default' : 'outline'}
                        onClick={() => setBookingType('passenger')}
                        className="rounded-none"
                        disabled={listing.seats_remaining < 1}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Seat
                      </Button>
                      <Button
                        type="button"
                        variant={bookingType === 'cargo' ? 'default' : 'outline'}
                        onClick={() => setBookingType('cargo')}
                        className="rounded-none"
                        disabled={!listing.cargo_space_available || listing.cargo_remaining_kg < 1}
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Cargo
                      </Button>
                    </div>
                  </div>

                  {/* Passenger Booking */}
                  {bookingType === 'passenger' && (
                    <div className="mb-4">
                      <Label>Number of seats</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSeatsToBook(Math.max(1, seatsToBook - 1))}
                          className="rounded-none"
                        >
                          -
                        </Button>
                        <span className="w-12 text-center font-bold">{seatsToBook}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSeatsToBook(Math.min(listing.seats_remaining, seatsToBook + 1))}
                          className="rounded-none"
                        >
                          +
                        </Button>
                        <span className="text-sm text-muted-foreground ml-2">
                          of {listing.seats_remaining} available
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        ₹{listing.price_per_seat} per seat
                      </p>
                    </div>
                  )}

                  {/* Cargo Booking */}
                  {bookingType === 'cargo' && (
                    <div className="mb-4">
                      <Label>Cargo weight (kg)</Label>
                      <Input
                        type="number"
                        min="1"
                        max={listing.cargo_remaining_kg}
                        value={cargoWeight}
                        onChange={(e) => setCargoWeight(Number(e.target.value))}
                        className="rounded-none mt-2"
                        placeholder="Enter weight in kg"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        ₹{listing.price_per_kg}/kg • {listing.cargo_remaining_kg}kg available
                      </p>
                    </div>
                  )}

                  {/* Pickup/Drop Points */}
                  <div className="mb-4">
                    <Label>Pickup point</Label>
                    <Input
                      value={pickupPoint}
                      onChange={(e) => setPickupPoint(e.target.value)}
                      className="rounded-none mt-2"
                      placeholder="Enter pickup location"
                    />
                  </div>

                  <div className="mb-4">
                    <Label>Drop point</Label>
                    <Input
                      value={dropPoint}
                      onChange={(e) => setDropPoint(e.target.value)}
                      className="rounded-none mt-2"
                      placeholder="Enter drop location"
                    />
                  </div>

                  <div className="mb-4">
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="rounded-none mt-2"
                      placeholder="Any special requests..."
                      rows={2}
                    />
                  </div>

                  {/* Price Summary */}
                  <div className="border-t border-border pt-4 mb-4">
                    <div className="space-y-2 text-sm">
                      {pricing.seatPrice > 0 && (
                        <div className="flex justify-between">
                          <span>Seats ({seatsToBook} × ₹{listing.price_per_seat})</span>
                          <span>₹{pricing.seatPrice}</span>
                        </div>
                      )}
                      {pricing.cargoPrice > 0 && (
                        <div className="flex justify-between">
                          <span>Cargo ({cargoWeight}kg × ₹{listing.price_per_kg})</span>
                          <span>₹{pricing.cargoPrice}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>Platform fee (2%)</span>
                        <span>₹{pricing.platformFee}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                        <span>Total</span>
                        <span className="text-accent">₹{pricing.total}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleBook}
                    disabled={booking || pricing.total === 0}
                    className="w-full rounded-none bg-accent hover:bg-accent/90"
                    data-testid="book-carpool-btn"
                  >
                    {booking ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Request to Book
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center mt-3">
                    Driver will confirm your booking request
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
